import { useEffect, useRef, useState } from 'react';
import {
  korpusy as api,
  admin,
  users as uzytkownicyApi,
  studies,
  type KorpusSzczegoly,
  type KorpusPozycja,
  type KorpusRegula,
  type RegulaPodglad,
  type RodzajWiedzy,
  type KorpusGrantUzytkownika,
  type KorpusDokumentDoWyboru,
  type KorpusZrodloDoWyboru,
} from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

type Zakladka = 'USTAWIENIA' | 'ZAWARTOSC' | 'REGULY' | 'DOSTEP' | 'WIEDZA';

/** Fallback, gdy katalog ról jest za uprawnieniem, którego zarządzający korpusami mieć nie musi. */
const ROLE_WBUDOWANE = ['OWNER', 'ADMIN', 'GEOLOGIST', 'ASSISTANT', 'OFFICE'];

/**
 * Typy reguł i kształt ich parametrów. Zamknięta lista po stronie serwera
 * ({@code CorpusRuleType}) — tu odwzorowana 1:1, bo nieznana wartość jest po cichu pomijana.
 */
const TYPY_REGUL = [
  { kod: 'ARCHIVE_BY_STUDY_TYPE', opis: 'Pliki archiwum wg typu opracowania', param: 'studyTypes' },
  { kod: 'ARCHIVE_BY_CATEGORY', opis: 'Pliki archiwum wg kategorii dokumentu', param: 'categories' },
  { kod: 'ARCHIVE_BY_YEAR', opis: 'Pliki archiwum wg rocznika', param: 'lata' },
  { kod: 'KNOWLEDGE_BY_KIND', opis: 'Treści z bazy wg rodzaju', param: 'kinds' },
  { kod: 'ARCHIVE_RISK_ATTACHMENTS', opis: 'Dokumenty źródłowe ocen ryzyka', param: null },
] as const;

const KATEGORIE_DOKUMENTOW = [
  'REGISTRATION', 'INSURANCE', 'TECHNICAL_INSPECTION', 'UDT', 'LEASING', 'INVOICE',
  'TACHOGRAPH', 'ADR', 'FUEL_CARD', 'ETOLL', 'PERMIT', 'MANUAL', 'PHOTO', 'OTHER',
];

const PUSTY_KORPUS: Partial<KorpusSzczegoly> = {
  code: '', name: '', description: '', systemPrompt: '',
  topK: 8, minScore: 0.3, sampleQuestions: '', active: true,
};

/**
 * Zarządzanie korpusami — odpowiedź na pytanie „co dokładnie widzi ten asystent".
 *
 * Zakładka „Zawartość" jest tu najważniejsza i dlatego stoi pierwsza po ustawieniach: pokazuje
 * POIMIENNIE każdą pozycję składu, z możliwością wyłączenia jej jednym kliknięciem. Reguły są
 * wygodą, ale to lista pozycji daje kontrolę.
 */
export function AsystentKorpusy() {
  const [lista, setLista] = useState<KorpusSzczegoly[]>([]);
  const [wybrany, setWybrany] = useState<KorpusSzczegoly | null>(null);
  const [nowy, setNowy] = useState(false);          // tryb tworzenia — formularz bez korpusu
  const [zakladka, setZakladka] = useState<Zakladka>('ZAWARTOSC');
  const [sklad, setSklad] = useState<KorpusPozycja[]>([]);
  const [reguly, setReguly] = useState<KorpusRegula[]>([]);
  const [dostep, setDostep] = useState<string[]>([]);
  const [grantyOsob, setGrantyOsob] = useState<KorpusGrantUzytkownika[]>([]);
  const [rodzaje, setRodzaje] = useState<RodzajWiedzy[]>([]);
  const [podglad, setPodglad] = useState<Record<number, RegulaPodglad>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [blad, setBlad] = useState<string | null>(null);

  // Formularz ustawień korpusu
  const [form, setForm] = useState<Partial<KorpusSzczegoly>>(PUSTY_KORPUS);

  // Wybierak materiałów
  const [dodawanie, setDodawanie] = useState<'PLIK' | 'ZRODLO' | null>(null);
  const [szukaj, setSzukaj] = useState('');
  const [pokazWrazliwe, setPokazWrazliwe] = useState(false);
  const [znalezioneDok, setZnalezioneDok] = useState<KorpusDokumentDoWyboru[]>([]);
  const [znalezioneZrd, setZnalezioneZrd] = useState<KorpusZrodloDoWyboru[]>([]);

  // Edytor reguł
  const [regulaEdyt, setRegulaEdyt] = useState<Partial<KorpusRegula> | null>(null);
  const [paramWart, setParamWart] = useState<string[]>([]);
  const [odRoku, setOdRoku] = useState('');
  const [doRoku, setDoRoku] = useState('');

  // Słowniki
  const [roleKatalog, setRoleKatalog] = useState<string[]>([]);
  const [osoby, setOsoby] = useState<{ id: number; fullName: string }[]>([]);
  const [typyOpracowan, setTypyOpracowan] = useState<{ code: string; name: string }[]>([]);
  const [notatki, setNotatki] = useState<KorpusZrodloDoWyboru[]>([]);

  const [notatkaTytul, setNotatkaTytul] = useState('');
  const [notatkaTresc, setNotatkaTresc] = useState('');

  useEffect(() => { odswiezListe(); }, []);
  useEffect(() => { if (wybrany && !nowy) wczytajSzczegoly(wybrany.id); }, [wybrany?.id, zakladka, nowy]);

  useEffect(() => {
    // Katalog ról jest za ADMIN_PERMISSIONS, którego zarządzający korpusami mieć nie musi —
    // brak dostępu to nie błąd, tylko powód, żeby użyć listy wbudowanej.
    admin.permissions.roles()
      .then(rs => setRoleKatalog(rs.filter(r => r.active).map(r => r.name)))
      .catch(() => { /* zostaje ROLE_WBUDOWANE */ });
    uzytkownicyApi.assignable()
      .then(u => setOsoby(u.map(x => ({ id: x.id, fullName: x.fullName }))))
      .catch(() => {});
    studies.types()
      .then(t => setTypyOpracowan(t.map(x => ({ code: x.code, name: x.name }))))
      .catch(() => {});
  }, []);

  const roleDoPokazania = roleKatalog.length ? roleKatalog : ROLE_WBUDOWANE;

  /**
   * Numer biegu — strażnik kolejności odpowiedzi.
   *
   * Bez niego szybkie przełączenie korpusu A → B zostawiało na ekranie skład A pod nagłówkiem
   * i podświetleniem B, jeśli wolniejsze zapytanie wróciło później.
   */
  const bieg = useRef(0);

  const wczytajSzczegoly = (id: number) => {
    setBlad(null);
    const moj = ++bieg.current;
    const gdyAktualne = <T,>(ustaw: (v: T) => void) => (v: T) => { if (moj === bieg.current) ustaw(v); };
    if (zakladka === 'ZAWARTOSC') api.sklad(id).then(gdyAktualne(setSklad)).catch(pokazBlad);
    if (zakladka === 'REGULY') api.reguly(id).then(gdyAktualne(setReguly)).catch(pokazBlad);
    if (zakladka === 'DOSTEP') {
      api.dostep(id).then(gdyAktualne(setDostep)).catch(pokazBlad);
      api.dostepUzytkownikow(id).then(gdyAktualne(setGrantyOsob)).catch(pokazBlad);
    }
    if (zakladka === 'WIEDZA') {
      api.rodzajeWiedzy().then(gdyAktualne(setRodzaje)).catch(pokazBlad);
      // Notatki to jedyny rodzaj wiedzy, który ktoś wpisał ręcznie — i jedyny, który wolno
      // usunąć. Bez tej listy przycisk „usuń" nie miałby skąd wziąć identyfikatora.
      api.zrodlaDoWyboru('')
        .then(gdyAktualne((z: KorpusZrodloDoWyboru[]) => setNotatki(z.filter(s => s.kod === 'NOTE'))))
        .catch(() => {});
    }
  };

  const odswiezListe = () =>
    api.lista().then(l => {
      setLista(l);
      setWybrany(w => (w ? l.find(x => x.id === w.id) ?? l[0] ?? null : l[0] ?? null));
    }).catch(e => setBlad(e?.message ?? 'Nie udało się wczytać korpusów'));

  const pokazBlad = (e: any) => setBlad(e?.message ?? 'Operacja nie powiodła się');
  const pokazMsg = (t: string) => { setMsg(t); setTimeout(() => setMsg(null), 5000); };

  // ── Ustawienia korpusu ──────────────────────────────────────────────

  const otworzNowy = () => {
    setNowy(true); setZakladka('USTAWIENIA'); setForm({ ...PUSTY_KORPUS }); setBlad(null);
  };

  const otworzEdycje = (k: KorpusSzczegoly) => {
    setNowy(false); setWybrany(k); setZakladka('USTAWIENIA');
    setForm({
      code: k.code, name: k.name, description: k.description ?? '',
      systemPrompt: k.systemPrompt ?? '', topK: k.topK, minScore: k.minScore,
      sampleQuestions: k.sampleQuestions ?? '', active: k.active,
    });
  };

  const zapiszUstawienia = async () => {
    if (!form.code?.trim() || !form.name?.trim()) {
      setBlad('Kod i nazwa korpusu są wymagane.');
      return;
    }
    try {
      // Komplet pól ZAWSZE: PUT nadpisuje bezwarunkowo, więc pominięcie `description`
      // wyczyściłoby opis, a pominięcie `active` cicho reaktywowałoby wyłączony korpus.
      const body = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description ?? '',
        systemPrompt: form.systemPrompt ?? '',
        topK: Math.max(1, Math.min(50, Number(form.topK) || 8)),
        minScore: Math.max(0, Math.min(1, Number(form.minScore) || 0)),
        sampleQuestions: form.sampleQuestions ?? '',
        active: form.active !== false,
      };
      const zapisany = nowy ? await api.utworz(body) : await api.zapisz(wybrany!.id, body);
      pokazMsg(nowy ? `Utworzono korpus ${zapisany.code}.` : 'Zapisano ustawienia korpusu.');
      setNowy(false);
      await odswiezListe();
      setWybrany(zapisany);
    } catch (e) { pokazBlad(e); }
  };

  const usunKorpus = async () => {
    if (!wybrany) return;
    const potwierdzenie = window.prompt(
      `Usunięcie korpusu skasuje TAKŻE wszystkie oparte na nim rozmowy użytkowników — `
      + `bezpowrotnie, bo treść rozmów nie trafia do audytu.\n\n`
      + `Aby potwierdzić, wpisz kod korpusu: ${wybrany.code}`);
    if (potwierdzenie !== wybrany.code) return;
    try {
      // Serwer domyślnie ODMAWIA usunięcia korpusu z rozmowami; ta flaga to świadoma zgoda,
      // której właśnie udzielono wpisując kod.
      await api.usunKorpus(wybrany.id, true);
      pokazMsg(`Usunięto korpus ${wybrany.code}.`);
      setWybrany(null);
      await odswiezListe();
    } catch (e) { pokazBlad(e); }
  };

  // ── Skład ───────────────────────────────────────────────────────────

  const przelacz = async (p: KorpusPozycja) => {
    if (!wybrany) return;
    try { await api.przelacz(wybrany.id, p.id, !p.included); } catch (e) { return pokazBlad(e); }
    wczytajSzczegoly(wybrany.id);
    odswiezListe();
  };

  const usunPozycje = async (p: KorpusPozycja) => {
    if (!wybrany) return;
    if (!window.confirm(`Usunąć „${p.nazwa}" ze składu korpusu? Plik zostaje w archiwum.`)) return;
    try { await api.usunPozycje(wybrany.id, p.id); } catch (e) { return pokazBlad(e); }
    pokazMsg('Pozycja usunięta ze składu.');
    wczytajSzczegoly(wybrany.id);
    odswiezListe();
  };

  const szukajMaterialow = async () => {
    if (!dodawanie) return;
    try {
      if (dodawanie === 'PLIK') setZnalezioneDok(await api.dokumentyDoWyboru(szukaj, pokazWrazliwe));
      else setZnalezioneZrd(await api.zrodlaDoWyboru(szukaj));
    } catch (e) { pokazBlad(e); }
  };

  const dopnij = async (rodzaj: 'PLIK' | 'ZRODLO', id: number, nazwa: string) => {
    if (!wybrany) return;
    try {
      if (rodzaj === 'PLIK') await api.dodajDokument(wybrany.id, id);
      else await api.dodajZrodlo(wybrany.id, id);
      pokazMsg(`Dopięto do korpusu: ${nazwa}`);
    } catch (e) { return pokazBlad(e); }
    wczytajSzczegoly(wybrany.id);
    odswiezListe();
  };

  // ── Reguły ──────────────────────────────────────────────────────────

  const nowaRegula = () => {
    setRegulaEdyt({ ruleType: '', includeSensitive: false, active: true });
    setParamWart([]); setOdRoku(''); setDoRoku('');
  };

  const edytujRegule = (r: KorpusRegula) => {
    setRegulaEdyt({ ...r });
    try {
      const p = JSON.parse(r.paramsJson || '{}');
      setParamWart(p.studyTypes ?? p.categories ?? p.kinds ?? []);
      setOdRoku(p.fromYear != null ? String(p.fromYear) : '');
      setDoRoku(p.toYear != null ? String(p.toYear) : '');
    } catch { setParamWart([]); setOdRoku(''); setDoRoku(''); }
  };

  /** Kształt paramsJson zależy od typu — serwer czyta inny klucz dla każdego z nich. */
  const zbudujParams = (): string => {
    const typ = TYPY_REGUL.find(t => t.kod === regulaEdyt?.ruleType);
    if (!typ) return '{}';
    if (typ.param === 'studyTypes') return JSON.stringify({ studyTypes: paramWart });
    if (typ.param === 'categories') return JSON.stringify({ categories: paramWart });
    if (typ.param === 'kinds') return JSON.stringify({ kinds: paramWart });
    if (typ.param === 'lata') {
      return JSON.stringify({
        fromYear: odRoku ? Number(odRoku) : null,
        toYear: doRoku ? Number(doRoku) : null,
      });
    }
    return '{}';
  };

  const zapiszRegule = async () => {
    if (!wybrany || !regulaEdyt?.ruleType) {
      setBlad('Wybierz typ reguły.');
      return;
    }
    try {
      // `id` jedzie tylko przy edycji — jego pominięcie utworzyłoby duplikat zamiast aktualizacji.
      await api.zapiszRegule(wybrany.id, {
        ...(regulaEdyt.id ? { id: regulaEdyt.id } : {}),
        ruleType: regulaEdyt.ruleType,
        paramsJson: zbudujParams(),
        includeSensitive: !!regulaEdyt.includeSensitive,
        active: regulaEdyt.active !== false,
      });
      pokazMsg('Reguła zapisana. Zastosuj ją, żeby zmieniła skład korpusu.');
      setRegulaEdyt(null);
      wczytajSzczegoly(wybrany.id);
    } catch (e) { pokazBlad(e); }
  };

  const usunRegule = async (r: KorpusRegula) => {
    if (!wybrany) return;
    if (!window.confirm('Usunąć regułę? Pozycje, które wciągnęła, zostaną w składzie jako ręczne.')) return;
    try {
      const zostalo = await api.usunRegule(wybrany.id, r.id);
      // Serwer zwraca, ile pozycji przepisał na ręczne — bez tego użytkownik sądziłby,
      // że usunięcie reguły wyczyściło korpus.
      pokazMsg(`Reguła usunięta. ${zostalo} pozycji zostało w składzie jako ręczne.`);
    } catch (e) { return pokazBlad(e); }
    wczytajSzczegoly(wybrany.id);
    odswiezListe();
  };

  const zrobPodglad = async (r: KorpusRegula) => {
    if (!wybrany) return;
    try {
      const wynik = await api.podglad(wybrany.id, r);
      setPodglad(p => ({ ...p, [r.id]: wynik }));
    } catch (e) { pokazBlad(e); }
  };

  const zastosuj = async (r: KorpusRegula) => {
    if (!wybrany) return;
    try {
      const w = await api.zastosuj(wybrany.id, r.id);
      pokazMsg(`Zastosowano regułę — dopasowano ${w.dopasowane} pozycji.`);
      setPodglad(p => ({ ...p, [r.id]: w }));
      odswiezListe();
    } catch (e) { pokazBlad(e); }
  };

  // ── Dostęp ──────────────────────────────────────────────────────────

  const przelaczRole = async (rola: string, ma: boolean) => {
    if (!wybrany) return;
    try {
      if (ma) await api.odbierz(wybrany.id, rola);
      else await api.nadaj(wybrany.id, rola);
    } catch (e) { return pokazBlad(e); }
    wczytajSzczegoly(wybrany.id);
  };

  const nadajOsobie = async (userId: number) => {
    if (!wybrany || !userId) return;
    try { await api.nadajUzytkownikowi(wybrany.id, userId); } catch (e) { return pokazBlad(e); }
    wczytajSzczegoly(wybrany.id);
  };

  const odbierzOsobie = async (userId: number) => {
    if (!wybrany) return;
    try { await api.odbierzUzytkownikowi(wybrany.id, userId); } catch (e) { return pokazBlad(e); }
    wczytajSzczegoly(wybrany.id);
  };

  // ── Wiedza ──────────────────────────────────────────────────────────

  const dodajNotatke = async () => {
    if (!notatkaTytul.trim() || !notatkaTresc.trim()) return;
    try {
      await api.zapiszNotatke(null, notatkaTytul.trim(), notatkaTresc.trim());
      setNotatkaTytul(''); setNotatkaTresc('');
      pokazMsg('Notatka zapisana i zaindeksowana. Dodaj ją do korpusu w zakładce Zawartość.');
      if (wybrany) wczytajSzczegoly(wybrany.id);
    } catch (e) { pokazBlad(e); }
  };

  const usunNotatke = async (n: KorpusZrodloDoWyboru) => {
    if (!window.confirm(`Usunąć notatkę „${n.tytul}"? Zniknie ze wszystkich korpusów.`)) return;
    try { await api.usunNotatke(n.id); } catch (e) { return pokazBlad(e); }
    pokazMsg('Notatka usunięta.');
    if (wybrany) wczytajSzczegoly(wybrany.id);
    odswiezListe();
  };

  const odswiezWiedze = async (kod?: string) => {
    try {
      await api.odswiezWiedze(kod);
      pokazMsg('Zlecono przeliczenie. Wynik pojawi się po przetworzeniu kolejki.');
    } catch (e) { pokazBlad(e); }
  };

  const typRegulyEdyt = TYPY_REGUL.find(t => t.kod === regulaEdyt?.ruleType);

  return (
    <div className="space-y-4">
      {msg && <div className="px-3 py-2 rounded bg-green-100 text-green-500 text-[12px]">{msg}</div>}
      {blad && <div className="px-3 py-2 rounded bg-red-100 text-red-500 text-[12px]">{blad}</div>}

      <div className="flex gap-4">
        {/* ── Lista korpusów ─────────────────────────────────── */}
        <aside className="card w-64 flex-none">
          <div className="card-head">
            <div className="card-title">Korpusy</div>
            <button className="btn btn-water text-[11px] ml-auto" onClick={otworzNowy}>
              <Icon name="plus" /> Nowy
            </button>
          </div>
          {lista.map(k => (
            <div key={k.id}
                 onClick={() => { setNowy(false); setWybrany(k); }}
                 className={'px-3 py-2 border-b border-ink-100 cursor-pointer hover:bg-ink-50 '
                   + (!nowy && wybrany?.id === k.id ? 'bg-water-500/5 border-l-2 border-l-water-500' : '')}>
              <div className="text-[13px] font-semibold text-ink-800">
                {k.name}
                {!k.active && <span className="pill pill-gray ml-2">wyłączony</span>}
              </div>
              <div className="text-[10px] text-ink-400 font-mono">{k.code}</div>
              <div className="flex gap-2 mt-1">
                <span className="pill pill-gray">{k.sourceCount} źr.</span>
                <span className="pill pill-gray">{k.fragmentCount} fragm.</span>
                {k.staleCount > 0 && (
                  <span className="pill" style={{ background: 'var(--amber-100, #fdecc3)' }}>
                    {k.staleCount} nieakt.
                  </span>
                )}
              </div>
            </div>
          ))}
          {lista.length === 0 && <div className="p-3 text-[12px] text-ink-400">Brak korpusów.</div>}
        </aside>

        {/* ── Szczegóły ──────────────────────────────────────── */}
        <section className="card flex-1">
          {!wybrany && !nowy ? (
            <div className="p-8 text-center text-ink-400 text-[13px]">
              Wybierz korpus z listy albo utwórz nowy.
            </div>
          ) : (
            <>
              <div className="card-head">
                <div className="card-title">{nowy ? 'Nowy korpus' : wybrany?.name}</div>
                <div className="ml-auto flex gap-1">
                  {(nowy
                    ? (['USTAWIENIA'] as Zakladka[])
                    : (['USTAWIENIA', 'ZAWARTOSC', 'REGULY', 'DOSTEP', 'WIEDZA'] as Zakladka[])
                  ).map(z => (
                    <button key={z}
                            className={'btn text-[12px] ' + (zakladka === z ? 'btn-primary' : 'btn-secondary')}
                            onClick={() => {
                              setZakladka(z);
                              if (z === 'USTAWIENIA' && wybrany && !nowy) otworzEdycje(wybrany);
                            }}>
                      {z === 'USTAWIENIA' ? 'Ustawienia'
                        : z === 'ZAWARTOSC' ? 'Zawartość'
                        : z === 'REGULY' ? 'Reguły'
                        : z === 'DOSTEP' ? 'Dostęp' : 'Wiedza z bazy'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-4">
                {/* ── Ustawienia ──────────────────────────────── */}
                {zakladka === 'USTAWIENIA' && (
                  <div className="space-y-3 max-w-2xl">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[11px] text-ink-500">Kod (unikalny, WIELKIE LITERY)</label>
                        <input className="input font-mono" value={form.code ?? ''}
                               disabled={!nowy}
                               title={nowy ? '' : 'Kodu istniejącego korpusu nie da się zmienić'}
                               onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} />
                      </div>
                      <div>
                        <label className="text-[11px] text-ink-500">Nazwa</label>
                        <input className="input" value={form.name ?? ''}
                               onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] text-ink-500">Opis (widoczny nad czatem)</label>
                      <textarea className="input" rows={2} value={form.description ?? ''}
                                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-[11px] text-ink-500">
                        Prompt systemowy (puste = „odpowiadasz wyłącznie na podstawie materiałów")
                      </label>
                      <textarea className="input" rows={3} value={form.systemPrompt ?? ''}
                                onChange={e => setForm(f => ({ ...f, systemPrompt: e.target.value }))} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[11px] text-ink-500">
                          topK — ile fragmentów trafia do promptu (1–50)
                        </label>
                        <input className="input" type="number" min={1} max={50} value={form.topK ?? 8}
                               onChange={e => setForm(f => ({ ...f, topK: Number(e.target.value) }))} />
                      </div>
                      <div>
                        <label className="text-[11px] text-ink-500">
                          Próg podobieństwa (0–1) — poniżej asystent odpowiada „nie wiem"
                        </label>
                        <input className="input" type="number" min={0} max={1} step={0.05}
                               value={form.minScore ?? 0.3}
                               onChange={e => setForm(f => ({ ...f, minScore: Number(e.target.value) }))} />
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] text-ink-500">
                        Pytania przykładowe — tablica JSON, np. ["Jakie zagrożenia…", "Jaki termin…"]
                      </label>
                      <textarea className="input font-mono text-[11px]" rows={2}
                                value={form.sampleQuestions ?? ''}
                                onChange={e => setForm(f => ({ ...f, sampleQuestions: e.target.value }))} />
                    </div>
                    <label className="flex items-center gap-2 text-[12px] text-ink-600">
                      <input type="checkbox" checked={form.active !== false}
                             onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
                      Korpus aktywny — wyłączony znika rozmówcom, ale zostaje zarządzającym
                    </label>

                    <div className="flex gap-2 pt-2 border-t border-ink-100">
                      <button className="btn btn-water text-[12px]" onClick={zapiszUstawienia}>
                        <Icon name="check" /> {nowy ? 'Utwórz korpus' : 'Zapisz ustawienia'}
                      </button>
                      {nowy && (
                        <button className="btn btn-secondary text-[12px]" onClick={() => setNowy(false)}>
                          Anuluj
                        </button>
                      )}
                      {!nowy && wybrany && (
                        <button className="btn btn-secondary text-[12px] ml-auto text-red-500"
                                onClick={usunKorpus}>
                          <Icon name="x" /> Usuń korpus
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Zawartość ───────────────────────────────── */}
                {zakladka === 'ZAWARTOSC' && (
                  <>
                    <div className="flex items-start gap-2 mb-3">
                      <p className="text-[12px] text-ink-500 flex-1">
                        Dokładna lista materiałów, z których ten asystent korzysta. Wyłączenie pozycji
                        działa tylko tutaj — plik zostaje w archiwum i nadal działa w reszcie systemu.
                      </p>
                      <button className="btn btn-water text-[12px] flex-none"
                              onClick={() => { setDodawanie(d => d ? null : 'PLIK'); setZnalezioneDok([]); setZnalezioneZrd([]); }}>
                        <Icon name="plus" /> Dodaj materiał
                      </button>
                    </div>

                    {dodawanie && (
                      <div className="border border-ink-100 rounded p-3 mb-3 space-y-2">
                        <div className="flex gap-1">
                          {(['PLIK', 'ZRODLO'] as const).map(t => (
                            <button key={t}
                                    className={'btn text-[11px] ' + (dodawanie === t ? 'btn-primary' : 'btn-secondary')}
                                    onClick={() => setDodawanie(t)}>
                              {/* NIE „Wiedza z bazy" — tak nazywa się zakładka główna i dwa
                                  przyciski o identycznej treści na jednym ekranie mylą. */}
                              {t === 'PLIK' ? 'Plik z archiwum' : 'Źródło wiedzy'}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2 items-center">
                          <input className="input flex-1" placeholder="Szukaj po nazwie…"
                                 value={szukaj}
                                 onChange={e => setSzukaj(e.target.value)}
                                 onKeyDown={e => { if (e.key === 'Enter') szukajMaterialow(); }} />
                          <button className="btn btn-secondary text-[12px]" onClick={szukajMaterialow}>
                            <Icon name="search" /> Szukaj
                          </button>
                        </div>
                        {dodawanie === 'PLIK' && (
                          <label className="flex items-center gap-2 text-[11px] text-ink-500">
                            <input type="checkbox" checked={pokazWrazliwe}
                                   onChange={e => setPokazWrazliwe(e.target.checked)} />
                            Pokaż materiały wrażliwe (kadry, faktury, flota) — serwer i tak odmówi
                            ich ręcznego dopięcia; służą wyłącznie regule z jawnym dostępem
                          </label>
                        )}

                        <div className="max-h-64 overflow-y-auto border-t border-ink-100 pt-2">
                          {dodawanie === 'PLIK' && znalezioneDok.map(d => (
                            <div key={d.id} className="flex items-center gap-2 py-1 text-[12px]">
                              <span className="flex-1">
                                {d.nazwa}
                                {d.wrazliwy && <span className="pill ml-2 text-red-500">wrażliwy</span>}
                                {d.typOpracowania && <span className="pill pill-gray ml-2">{d.typOpracowania}</span>}
                              </span>
                              <button className="btn btn-secondary text-[11px]"
                                      onClick={() => dopnij('PLIK', d.id, d.nazwa)}>Dopnij</button>
                            </div>
                          ))}
                          {dodawanie === 'ZRODLO' && znalezioneZrd.map(z => (
                            <div key={z.id} className="flex items-center gap-2 py-1 text-[12px]">
                              <span className="flex-1">
                                {z.tytul}
                                <span className="pill pill-water ml-2">{z.rodzaj}</span>
                                {z.fragmenty != null && (
                                  <span className="text-[10px] text-ink-400 ml-2">{z.fragmenty} fragm.</span>
                                )}
                              </span>
                              <button className="btn btn-secondary text-[11px]"
                                      onClick={() => dopnij('ZRODLO', z.id, z.tytul)}>Dopnij</button>
                            </div>
                          ))}
                          {((dodawanie === 'PLIK' && znalezioneDok.length === 0)
                            || (dodawanie === 'ZRODLO' && znalezioneZrd.length === 0)) && (
                            <div className="text-[11px] text-ink-400 py-2">
                              Wpisz frazę i kliknij „Szukaj". Wyniki ograniczone do 50 pozycji.
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="max-h-[520px] overflow-y-auto overflow-x-auto">
                      <table className="table-hw">
                        <thead>
                          <tr>
                            <th className="w-20">Rodzaj</th><th>Nazwa</th>
                            <th className="w-24">Źródło</th><th className="w-20 text-right">Fragm.</th>
                            <th className="w-24">W korpusie</th><th className="w-16"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sklad.map(p => (
                            <tr key={p.id}>
                              <td>
                                <span className={'pill ' + (p.rodzaj === 'WIEDZA' ? 'pill-water' : 'pill-gray')}>
                                  {p.rodzaj === 'WIEDZA' ? 'baza' : 'plik'}
                                </span>
                              </td>
                              <td className="text-[12px]">
                                {p.nazwa}
                                {p.nieaktualne && (
                                  <span className="pill ml-2" title="Treść zmieniła się po ostatniej indeksacji">
                                    nieaktualne
                                  </span>
                                )}
                              </td>
                              <td className="text-[11px] text-ink-400">
                                {p.origin === 'RULE' ? 'reguła' : 'ręcznie'}
                              </td>
                              <td className="text-[11px] font-mono text-right tabular-nums">
                                {p.fragmenty ?? '—'}
                              </td>
                              <td>
                                <button className={'btn text-[11px] ' + (p.included ? 'btn-water' : 'btn-secondary')}
                                        onClick={() => przelacz(p)}>
                                  {p.included ? 'tak' : 'nie'}
                                </button>
                              </td>
                              <td>
                                <button className="btn-ghost text-[11px] text-red-500"
                                        title="Usuń pozycję ze składu"
                                        onClick={() => usunPozycje(p)}>
                                  <Icon name="x" />
                                </button>
                              </td>
                            </tr>
                          ))}
                          {sklad.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-ink-400 text-[12px] py-6">
                              Korpus jest pusty — dodaj materiał przyciskiem wyżej albo zastosuj regułę.
                            </td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* ── Reguły ──────────────────────────────────── */}
                {zakladka === 'REGULY' && (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <p className="text-[12px] text-ink-500 flex-1">
                        Reguła dobiera materiały automatycznie. Podgląd pokazuje, co wejdzie do korpusu,
                        ZANIM cokolwiek zapiszesz. Zapisanie reguły jej nie stosuje — to osobna decyzja.
                      </p>
                      <button className="btn btn-water text-[12px] flex-none" onClick={nowaRegula}>
                        <Icon name="plus" /> Nowa reguła
                      </button>
                    </div>

                    {regulaEdyt && (
                      <div className="border border-water-500/40 rounded p-3 space-y-2">
                        <div className="text-[13px] font-semibold text-ink-800">
                          {regulaEdyt.id ? 'Edycja reguły' : 'Nowa reguła'}
                        </div>
                        <SearchableSelect
                          value={regulaEdyt.ruleType ?? ''}
                          onChange={v => { setRegulaEdyt(r => ({ ...r, ruleType: v })); setParamWart([]); }}
                          options={TYPY_REGUL.map(t => ({ value: t.kod, label: t.opis }))}
                          placeholder="Wybierz typ reguły"
                        />

                        {typRegulyEdyt?.param === 'studyTypes' && (
                          <WybieraczWielu
                            etykieta="Typy opracowań"
                            opcje={typyOpracowan.map(t => ({ value: t.code, label: t.name }))}
                            wybrane={paramWart} ustaw={setParamWart} />
                        )}
                        {typRegulyEdyt?.param === 'categories' && (
                          <WybieraczWielu
                            etykieta="Kategorie dokumentów"
                            opcje={KATEGORIE_DOKUMENTOW.map(c => ({ value: c, label: c }))}
                            wybrane={paramWart} ustaw={setParamWart} />
                        )}
                        {typRegulyEdyt?.param === 'kinds' && (
                          <WybieraczWielu
                            etykieta="Rodzaje wiedzy z bazy"
                            opcje={rodzaje.map(r => ({ value: r.kod, label: r.opis }))}
                            wybrane={paramWart} ustaw={setParamWart} />
                        )}
                        {typRegulyEdyt?.param === 'lata' && (
                          <div className="flex gap-2 items-center">
                            <input className="input w-28" type="number" placeholder="od roku"
                                   value={odRoku} onChange={e => setOdRoku(e.target.value)} />
                            <span className="text-[12px] text-ink-400">—</span>
                            <input className="input w-28" type="number" placeholder="do roku"
                                   value={doRoku} onChange={e => setDoRoku(e.target.value)} />
                            <span className="text-[11px] text-ink-400">
                              obie puste = reguła nie dopasuje niczego
                            </span>
                          </div>
                        )}
                        {typRegulyEdyt?.param === null && (
                          <div className="text-[11px] text-ink-400">Ta reguła nie ma parametrów.</div>
                        )}

                        <label className="flex items-center gap-2 text-[12px] text-ink-600">
                          <input type="checkbox" checked={!!regulaEdyt.includeSensitive}
                                 onChange={e => setRegulaEdyt(r => ({ ...r, includeSensitive: e.target.checked }))} />
                          Wciągaj także materiały wrażliwe — umowy kadrowe, faktury KSeF, dokumenty floty
                        </label>
                        {regulaEdyt.includeSensitive && (
                          <div className="text-[11px] text-red-500">
                            Te dokumenty są bramkowane osobnymi uprawnieniami (HR_SENSITIVE, FINANCE_VIEW,
                            FLEET_VIEW). Po wciągnięciu do korpusu przeczyta je każdy, kto ma do niego dostęp.
                          </div>
                        )}
                        <div className="flex gap-2">
                          <button className="btn btn-water text-[12px]" onClick={zapiszRegule}>
                            Zapisz regułę
                          </button>
                          <button className="btn btn-secondary text-[12px]" onClick={() => setRegulaEdyt(null)}>
                            Anuluj
                          </button>
                        </div>
                      </div>
                    )}

                    {reguly.map(r => {
                      const p = podglad[r.id];
                      return (
                        <div key={r.id} className="border border-ink-100 rounded p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-[13px] font-semibold text-ink-800">{r.opis}</span>
                            {!r.includeSensitive && (
                              <span className="pill pill-gray" title="Pomija dokumenty kadr, faktur i floty">
                                bez wrażliwych
                              </span>
                            )}
                            {!r.active && <span className="pill pill-gray">nieaktywna</span>}
                            <div className="ml-auto flex gap-2">
                              <button className="btn btn-secondary text-[11px]" onClick={() => zrobPodglad(r)}>
                                Podgląd
                              </button>
                              <button className="btn btn-water text-[11px]" onClick={() => zastosuj(r)}>
                                Zastosuj
                              </button>
                              <button className="btn btn-secondary text-[11px]" onClick={() => edytujRegule(r)}>
                                Edytuj
                              </button>
                              <button className="btn-ghost text-[11px] text-red-500" title="Usuń regułę"
                                      onClick={() => usunRegule(r)}>
                                <Icon name="x" />
                              </button>
                            </div>
                          </div>
                          <div className="text-[11px] font-mono text-ink-400 mt-1">{r.paramsJson}</div>
                          {p && (
                            <div className="mt-2 text-[12px] text-ink-600">
                              Dopasowano <b>{p.dopasowane}</b> pozycji
                              {p.pominieteWrazliwe > 0 && (
                                <> · pominięto <b>{p.pominieteWrazliwe}</b> jako powiązane
                                   z modułami wrażliwymi (kadry, faktury, flota)</>
                              )}
                            </div>
                          )}
                          {r.lastAppliedAt && (
                            <div className="text-[10px] text-ink-300 mt-1">
                              Ostatnio zastosowana: {new Date(r.lastAppliedAt).toLocaleString('pl-PL')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {reguly.length === 0 && !regulaEdyt && (
                      <div className="text-[12px] text-ink-400">Ten korpus nie ma reguł — skład jest ręczny.</div>
                    )}
                  </div>
                )}

                {/* ── Dostęp ──────────────────────────────────── */}
                {zakladka === 'DOSTEP' && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-[12px] text-ink-500 mb-2">
                        Role, które mogą rozmawiać z tym asystentem. Dostęp do korpusu = dostęp do
                        wszystkiego, co w nim jest.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {roleDoPokazania.map(r => {
                          const ma = dostep.includes(r);
                          return (
                            <button key={r}
                                    className={'btn text-[12px] ' + (ma ? 'btn-water' : 'btn-secondary')}
                                    onClick={() => przelaczRole(r, ma)}>
                              {ma && <Icon name="check" />} {r}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="border-t border-ink-100 pt-3">
                      <p className="text-[12px] text-ink-500 mb-2">
                        Dostęp imienny — węższy niż rola. Przydaje się, gdy korpus ma zobaczyć jedna
                        osoba, a nie cała jej rola.
                      </p>
                      <div className="flex gap-2 items-center mb-2 max-w-md">
                        <SearchableSelect
                          value=""
                          onChange={v => v && nadajOsobie(Number(v))}
                          options={osoby
                            .filter(o => !grantyOsob.some(g => g.userId === o.id))
                            .map(o => ({ value: String(o.id), label: o.fullName }))}
                          placeholder="Dodaj osobę…"
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {grantyOsob.map(g => (
                          <span key={g.userId}
                                className="pill pill-water flex items-center gap-1">
                            {g.imieNazwisko}
                            <button className="btn-ghost text-[10px]" title="Odbierz dostęp"
                                    onClick={() => odbierzOsobie(g.userId)}>
                              <Icon name="x" />
                            </button>
                          </span>
                        ))}
                        {grantyOsob.length === 0 && (
                          <span className="text-[11px] text-ink-400">Brak dostępów imiennych.</span>
                        )}
                      </div>
                    </div>

                    <div className="text-[11px] text-ink-400">
                      OWNER i ADMIN mają dostęp niezależnie od tej listy — przez uprawnienie
                      zarządzania korpusami.
                      {wybrany && !wybrany.active && (
                        <div className="text-amber-500 mt-1">
                          Korpus jest WYŁĄCZONY — nadane tu dostępy nie zadziałają, dopóki go nie włączysz
                          w zakładce Ustawienia.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Wiedza z bazy ───────────────────────────── */}
                {zakladka === 'WIEDZA' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <p className="text-[12px] text-ink-500 flex-1">
                        Treści z bazy odtwarzane automatycznie. Przeliczenie dotyka tylko tego,
                        czego treść faktycznie się zmieniła.
                      </p>
                      <button className="btn btn-secondary text-[12px]" onClick={() => odswiezWiedze()}>
                        Przelicz wszystko
                      </button>
                    </div>
                    <div className="max-h-80 overflow-y-auto overflow-x-auto">
                      <table className="table-hw">
                        <thead>
                          <tr><th>Rodzaj</th><th className="w-20 text-right">Źródeł</th>
                              <th className="w-24 text-right">Fragmentów</th>
                              <th className="w-24 text-right">Nieaktualnych</th><th className="w-24"></th></tr>
                        </thead>
                        <tbody>
                          {rodzaje.map(r => (
                            <tr key={r.kod}>
                              <td className="text-[12px]">{r.opis}</td>
                              <td className="text-[11px] font-mono text-right tabular-nums">{r.zrodel}</td>
                              <td className="text-[11px] font-mono text-right tabular-nums">{r.fragmentow}</td>
                              <td className="text-[11px] font-mono text-right tabular-nums">
                                {r.nieaktualnych > 0
                                  ? <span className="text-amber-500 font-semibold">{r.nieaktualnych}</span>
                                  : '0'}
                              </td>
                              <td>
                                <button className="btn btn-secondary text-[11px]"
                                        onClick={() => odswiezWiedze(r.kod)}>Przelicz</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="border-t border-ink-100 pt-3">
                      <div className="text-[13px] font-semibold text-ink-800 mb-1">Notatka własna</div>
                      <p className="text-[12px] text-ink-500 mb-2">
                        Wiedza, której nie ma w żadnym pliku ani tabeli. Żaden mechanizm jej nie
                        nadpisze — jako jedyny rodzaj wiedzy nie pochodzi z providera.
                      </p>
                      <input className="input mb-2" placeholder="Tytuł notatki"
                             value={notatkaTytul} onChange={e => setNotatkaTytul(e.target.value)} />
                      <textarea className="input mb-2" rows={4} placeholder="Treść…"
                                value={notatkaTresc} onChange={e => setNotatkaTresc(e.target.value)} />
                      <button className="btn btn-water text-[12px]"
                              disabled={!notatkaTytul.trim() || !notatkaTresc.trim()}
                              onClick={dodajNotatke}>
                        <Icon name="plus" /> Zapisz notatkę
                      </button>

                      {notatki.length > 0 && (
                        <div className="mt-3">
                          <div className="text-[11px] uppercase tracking-wider text-ink-400 mb-1">
                            Zapisane notatki
                          </div>
                          {notatki.map(n => (
                            <div key={n.id} className="flex items-center gap-2 py-1 text-[12px]">
                              <span className="flex-1">{n.tytul}</span>
                              <span className="text-[10px] text-ink-400">{n.fragmenty ?? 0} fragm.</span>
                              <button className="btn-ghost text-[11px] text-red-500"
                                      title="Usuń notatkę" onClick={() => usunNotatke(n)}>
                                <Icon name="x" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

/** Odhaczanie wielu wartości słownikowych — parametry reguł to zawsze listy kodów enuma. */
function WybieraczWielu({ etykieta, opcje, wybrane, ustaw }: {
  etykieta: string;
  opcje: { value: string; label: string }[];
  wybrane: string[];
  ustaw: (v: string[]) => void;
}) {
  return (
    <div>
      <div className="text-[11px] text-ink-500 mb-1">{etykieta}</div>
      <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
        {opcje.map(o => {
          const ma = wybrane.includes(o.value);
          return (
            <button key={o.value}
                    className={'btn text-[11px] ' + (ma ? 'btn-water' : 'btn-secondary')}
                    onClick={() => ustaw(ma ? wybrane.filter(x => x !== o.value) : [...wybrane, o.value])}>
              {o.label}
            </button>
          );
        })}
        {opcje.length === 0 && (
          <span className="text-[11px] text-ink-400">Brak wartości do wyboru.</span>
        )}
      </div>
    </div>
  );
}
